/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-10
 */

// Add third-party dependencies.
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

// Add local dependencies.
import { useSessionStatusQuery } from '../features/auth';
import type { RootState } from '../store';

/**
 * Custom hook for managing profile data fetching and state.
 *
 * Features:
 * - Automatically fetches fresh profile data on mount
 * - Provides loading states for profile data
 * - Integrates with existing RTK Query cache
 * - Handles authentication state changes
 * - Provides profile-specific utilities
 *
 * @returns Profile data state and utilities
 * @example
 * const { user, profile, isLoading, isError, refetch } = useProfileData();
 */
export const useProfileData = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  // Use sessionStatus query to get fresh user data including profile
  const {
    data: sessionData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useSessionStatusQuery(undefined, {
    // Only fetch if authenticated
    skip: !isAuthenticated,
    // Refetch on mount and when window regains focus
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    // Keep data fresh
    refetchOnReconnect: true,
  });

  // Extract profile data from user
  const profile = user?.profile || null;

  /**
   * Check if user has complete profile information.
   */
  const hasCompleteProfile = () => {
    if (!user) return false;

    const hasBasicInfo = !!(user.first_name && user.last_name && user.email);
    const hasProfileData = !!(
      profile?.bio ||
      profile?.title ||
      profile?.company
    );

    return hasBasicInfo && hasProfileData;
  };

  /**
   * Check if user has avatar.
   */
  const hasAvatar = () => !!(profile?.avatar || profile?.has_avatar);

  /**
   * Check if user has address information.
   */
  const hasAddress = () => !!(profile?.address || profile?.has_address);

  /**
   * Get avatar URL with fallback.
   */
  const getAvatarUrl = () => profile?.avatar_url || null;

  /**
   * Get user's full name with fallback.
   */
  const getDisplayName = () => {
    if (user?.full_name) return user.full_name;
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.first_name) return user.first_name;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  /**
   * Force refresh profile data.
   */
  const refreshProfile = () => refetch();

  return {
    // Core data
    user,
    profile,
    sessionData,

    // Loading states
    isLoading,
    isFetching,
    isError,
    error,

    // Profile utilities
    hasCompleteProfile: hasCompleteProfile(),
    hasAvatar: hasAvatar(),
    hasAddress: hasAddress(),
    avatarUrl: getAvatarUrl(),
    displayName: getDisplayName(),

    // Actions
    refetch: refreshProfile,
  };
};

/**
 * Hook for components that specifically need profile data and loading states.
 * Provides a more focused interface for profile-related components.
 *
 * @returns Profile-specific data and states
 * @example
 * const { profile, isProfileLoading, profileError, refreshProfile } = useProfile();
 */
export const useProfile = () => {
  const {
    profile,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    hasCompleteProfile,
    hasAvatar,
    hasAddress,
    avatarUrl,
  } = useProfileData();

  return {
    // Profile data
    profile,

    // Loading states (renamed for clarity)
    isProfileLoading: isLoading,
    isProfileFetching: isFetching,
    isProfileError: isError,
    profileError: error,

    // Profile status
    hasCompleteProfile,
    hasAvatar,
    hasAddress,
    avatarUrl,

    // Actions
    refreshProfile: refetch,
  };
};

/**
 * Hook for form components that need to initialize with current profile data.
 * Provides form-specific utilities and state management.
 *
 * @returns Form-specific profile data and utilities
 * @example
 * const { initialData, isDataReady, dataError } = useProfileFormData();
 */
export const useProfileFormData = () => {
  const { user, profile, isLoading, isError, error } = useProfileData();

  // Check if we have enough data to initialize forms
  const isDataReady = !isLoading && !!user;

  // Memoize initial form data to prevent object recreation on every render
  const initialData = useMemo(() => {
    if (!user) return null;

    // The UnifiedUserProfileSerializer flattens profile fields to the user level
    // So we need to check both user-level fields and nested profile fields
    return {
      // User fields
      email: user.email || '',
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      phoneNumber: user.phone_number || '',
      timezone: user.timezone || 'UTC',
      language: user.language || 'en',
      marketingEmails: user.marketing_emails || false,

      // Profile fields - check both flattened (user-level) and nested (profile-level)
      bio: user.bio || profile?.bio || '',
      title: user.title || profile?.title || '',
      company: user.company || profile?.company || '',
      website: user.website || profile?.website || '',
      linkedin: user.linkedin || profile?.linkedin || '',
      instagram: user.instagram || profile?.instagram || '',
      facebook: user.facebook || profile?.facebook || '',

      // Address fields - check both flattened (user-level) and nested (profile-level)
      streetAddress1:
        user.address?.street_address_1 ||
        profile?.address?.street_address_1 ||
        '',
      streetAddress2:
        user.address?.street_address_2 ||
        profile?.address?.street_address_2 ||
        '',
      city: user.address?.city || profile?.address?.city || '',
      stateProvince:
        user.address?.state_province || profile?.address?.state_province || '',
      postalCode:
        user.address?.postal_code || profile?.address?.postal_code || '',
      country: user.address?.country || profile?.address?.country || '',

      // Privacy settings - check both flattened (user-level) and nested (profile-level)
      addressVisible: user.address_visible ?? profile?.address_visible ?? false,
      emailVisible: user.email_visible ?? profile?.email_visible ?? false,
      phoneVisible: user.phone_visible ?? profile?.phone_visible ?? false,

      // Notification preferences - check both flattened (user-level) and nested (profile-level)
      emailNotifications:
        user.email_notifications ?? profile?.email_notifications ?? true,
      smsNotifications:
        user.sms_notifications ?? profile?.sms_notifications ?? false,
      pushNotifications:
        user.push_notifications ?? profile?.push_notifications ?? true,
    };
  }, [user, profile]);

  return {
    // Data state
    initialData,
    isDataReady,
    isDataLoading: isLoading,
    dataError: isError ? error : null,

    // Raw data access
    user,
    profile,
  };
};

export default useProfileData;
